import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "app/providers/AuthProvider";
import useNotification from "component/global/event-handler/hooks/use-notification";

const normalizePath = (value?: string | null) => {
    if (!value) return null;

    const trimmed = value.trim();
    if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//")) return null;

    return trimmed.split(/[?#]/, 1)[0] || "/";
};

const pathMatches = (path: string, pattern: string, matchType: string) => {
    if (!pattern) return false;

    const normalizedPattern = normalizePath(pattern);
    if (!normalizedPattern) return false;

    if (matchType === "prefix") {
        return path === normalizedPattern || path.startsWith(`${normalizedPattern}/`);
    }

    return path === normalizedPattern;
};

const RouteAccessGuard = () => {
    const { user } = useAuth();
    const location = useLocation();
    const notification = useNotification();
    const lastWarningKeyRef = useRef<string | null>(null);
    const routeAccess = user?.routeAccess;
    const currentPath = location.pathname;
    const redirectPath = normalizePath(routeAccess?.redirectTo);

    const isAllowed = useMemo(() => {
        if (!routeAccess?.restricted) return true;

        return routeAccess.allowedPaths.some((allowedPath) =>
            pathMatches(currentPath, allowedPath.pattern, allowedPath.matchType)
        );
    }, [currentPath, routeAccess]);

    const isAtRedirectTarget = Boolean(redirectPath && currentPath === redirectPath);

    useEffect(() => {
        if (!routeAccess?.restricted || isAllowed || isAtRedirectTarget || !routeAccess.message) return;

        const warningKey = `${currentPath}:${routeAccess.message}`;
        if (lastWarningKeyRef.current === warningKey) return;

        lastWarningKeyRef.current = warningKey;

        notification.warning("ROUTE_ACCESS_RESTRICTED", routeAccess.message);
    }, [
        currentPath,
        isAllowed,
        isAtRedirectTarget,
        notification,
        routeAccess?.message,
        routeAccess?.restricted,
    ]);

    if (routeAccess?.restricted && !isAllowed && redirectPath && !isAtRedirectTarget) {
        return <Navigate to={redirectPath} replace />;
    }

    return <Outlet />;
};

export default RouteAccessGuard;
